import { MESSAGE_TYPE } from "@/domains/messages/models/constants";
import { isoDaysFromNow } from "@/mocks/dates";
import { PERSONA_ID, type PersonaId, PERSONAS } from "@/mocks/personas";
import type { MessageWire } from "@/mocks/types";

type MessageSeed = Pick<MessageWire, "type" | "content"> & { daysAgo: number };

const THREAD_IDS: Record<PersonaId, number> = {
  [PERSONA_ID.APPROVED]: 7101,
  [PERSONA_ID.IN_REVIEW]: 7201,
  [PERSONA_ID.SUBSCRIBED]: 7301,
  [PERSONA_ID.NOT_SUBSCRIBED]: 7401,
};

const MESSAGE_SEEDS: Record<PersonaId, MessageSeed[]> = {
  [PERSONA_ID.APPROVED]: [
    {
      type: MESSAGE_TYPE.SYSTEM,
      daysAgo: 1.2,
      content: "Your care plan is ready. Review it any time from the Care Plan tab.",
    },
    {
      type: MESSAGE_TYPE.CLINICIAN,
      daysAgo: 1.1,
      content: `Hi ${PERSONAS[PERSONA_ID.APPROVED].firstName}, welcome to EO Care! This is a sample message from your care team. We built your first plan around a small morning dose, an inhaler for flare-ups and a gummy for better nights.\n\nStart low, take it at the same time every day, and message us here whenever something feels off. We usually reply within one business day.`,
    },
  ],
  [PERSONA_ID.IN_REVIEW]: [
    {
      type: MESSAGE_TYPE.SYSTEM,
      daysAgo: 0.05,
      content:
        "Thanks for completing your profile! Our clinical team is reviewing your answers and will publish your care plan shortly. We will let you know as soon as it is ready.",
    },
  ],
  [PERSONA_ID.SUBSCRIBED]: [
    {
      type: MESSAGE_TYPE.SYSTEM,
      daysAgo: 62,
      content: "Your care plan is ready. Review it any time from the Care Plan tab.",
    },
    {
      type: MESSAGE_TYPE.USER,
      daysAgo: 22,
      content: "The morning tincture is helping, but late afternoons are still tough.",
    },
    {
      type: MESSAGE_TYPE.CLINICIAN,
      daysAgo: 21.9,
      content:
        "Thanks for letting us know. This is a sample reply: we raised the inhaler dose to 1 to 2 puffs for afternoon flare-ups. The changes are highlighted on your care plan.",
    },
    { type: MESSAGE_TYPE.EVENT, daysAgo: 21.8, content: "Your care plan was updated" },
    {
      type: MESSAGE_TYPE.CLINICIAN,
      daysAgo: 1,
      content:
        "It is time for your monthly check-in. It only takes a few minutes and helps us fine-tune your plan.",
    },
  ],
  [PERSONA_ID.NOT_SUBSCRIBED]: [
    {
      type: MESSAGE_TYPE.SYSTEM,
      daysAgo: 95,
      content: "Your care plan is ready. Review it any time from the Care Plan tab.",
    },
    {
      type: MESSAGE_TYPE.CLINICIAN,
      daysAgo: 46,
      content:
        "This is a sample update from your care team: we replaced the Morning Focus Capsules with the Daily Balance Tincture, which is easier to adjust.",
    },
    { type: MESSAGE_TYPE.EVENT, daysAgo: 40, content: "Subscription cancelled" },
    {
      type: MESSAGE_TYPE.CLINICIAN,
      daysAgo: 3,
      content:
        "We noticed your subscription is no longer active. Whenever you are ready to restart, your plan is waiting in your cart.",
    },
  ],
};

export const getThreadId = (personaId: PersonaId) => {
  return THREAD_IDS[personaId];
};

export const createInitialMessages = (personaId: PersonaId): MessageWire[] => {
  const threadId = getThreadId(personaId);

  return MESSAGE_SEEDS[personaId].map(({ content, daysAgo, type }, index) => {
    return {
      id: threadId * 100 + index + 1,
      threadId,
      type,
      content,
      metadata: [],
      createdAt: isoDaysFromNow(-daysAgo),
    };
  });
};
